import type { RawQuestionSettings } from '@/features/content/useComponent';

import type { AttemptField, AttemptSource } from './attemptSettings';

// What a stored hint or solutions setting means. The one reader for the student
// view, the settings dialogs and the course's panel, so they cannot disagree
// (issue #144).
//
//   Hints, one setting for each kind of question:
//     -1                       always
//     1 and above              after that many attempts
//     0, or nothing saved      only with the solutions
//
//   Solutions:
//     1, 2, 3                  1 hour, 1 day or 1 week after the due date
//     4                        never
//     anything else, or        at the due date. 5, 6 and 7 can be stored: the
//     nothing saved            course's list used to offer the bare numbers.
//
// A chapter's value wins over its course's, and the course's over the default,
// one setting at a time. Unlike an attempts number, a stored 0 is a choice at
// either level, and it overrides the level below it.

export type HintField = AttemptField;
export type TimingSource = AttemptSource;

export interface ResolvedTimingSetting {
  source: TimingSource;
  value: number;
}

export interface TimingChoice {
  label: string;
  value: number;
}

const HINTS_ALWAYS = -1;
const HINTS_WITH_SOLUTIONS = 0;
const SOLUTIONS_AT_DUE_DATE = 0;

const HINT_KEYS: Record<HintField, readonly [string, string]> = {
  exactAnswer: [
    'num_attempts_to_show_hints_exact_answer',
    'numAttemptsToShowHintsExactAnswer',
  ],
  multipleChoice: [
    'num_attempts_to_show_hints_multiple_choice',
    'numAttemptsToShowHintsMultipleChoice',
  ],
};
const SOLUTIONS_KEYS = [
  'show_solutions_after_interval',
  'showSolutionsAfterInterval',
] as const;

const LISTED_HINT_ATTEMPTS = Array.from({ length: 10 }, (_unused, index) => index + 1);

export const SOLUTIONS_CHOICES: readonly TimingChoice[] = [
  { label: 'At the due date', value: SOLUTIONS_AT_DUE_DATE },
  { label: '1 hour after the due date', value: 1 },
  { label: '1 day after the due date', value: 2 },
  { label: '1 week after the due date', value: 3 },
  { label: 'Never', value: 4 },
];

// The stored number, or undefined when the level decides nothing.
function readStored(
  settings: RawQuestionSettings | null | undefined,
  [snakeKey, camelKey]: readonly [string, string],
): number | undefined {
  if (!settings) {
    return undefined;
  }

  const record = settings as Record<string, unknown>;
  const raw = record[snakeKey] ?? record[camelKey];
  const value =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : Number.NaN;

  return Number.isFinite(value) ? value : undefined;
}

export function readHintSetting(
  settings: RawQuestionSettings | null | undefined,
  field: HintField,
): number | undefined {
  return readStored(settings, HINT_KEYS[field]);
}

export function readSolutionsSetting(
  settings: RawQuestionSettings | null | undefined,
): number | undefined {
  return readStored(settings, SOLUTIONS_KEYS);
}

// The value in the list that does what a stored value does.
export function solutionsChoiceValue(stored: number): number {
  return SOLUTIONS_CHOICES.some((choice) => choice.value === stored)
    ? stored
    : SOLUTIONS_AT_DUE_DATE;
}

function resolve(
  chapterValue: number | undefined,
  courseValue: number | undefined,
  defaultValue: number,
): ResolvedTimingSetting {
  if (chapterValue !== undefined) {
    return { source: 'chapter', value: chapterValue };
  }

  if (courseValue !== undefined) {
    return { source: 'course', value: courseValue };
  }

  return { source: 'default', value: defaultValue };
}

export function resolveHintSetting(
  chapterSettings: RawQuestionSettings | null | undefined,
  courseSettings: RawQuestionSettings | null | undefined,
  field: HintField,
): ResolvedTimingSetting {
  return resolve(
    readHintSetting(chapterSettings, field),
    readHintSetting(courseSettings, field),
    HINTS_WITH_SOLUTIONS,
  );
}

export function resolveSolutionsSetting(
  chapterSettings: RawQuestionSettings | null | undefined,
  courseSettings: RawQuestionSettings | null | undefined,
): ResolvedTimingSetting {
  const { source, value } = resolve(
    readSolutionsSetting(chapterSettings),
    readSolutionsSetting(courseSettings),
    SOLUTIONS_AT_DUE_DATE,
  );

  return { source, value: solutionsChoiceValue(value) };
}

export function describeHintSetting(value: number): string {
  if (value === HINTS_ALWAYS) {
    return 'Always';
  }

  if (value > 0) {
    return `After ${value} attempt${value === 1 ? '' : 's'}`;
  }

  return 'Only with the solutions';
}

export function describeSolutionsSetting(value: number): string {
  const choice = SOLUTIONS_CHOICES.find(
    (candidate) => candidate.value === solutionsChoiceValue(value),
  );

  return choice?.label ?? '';
}

// Always, 1 to 10, then only with the solutions, plus a stored number of
// attempts outside the list, so the select never shows a value other than the
// one in effect.
export function hintChoices(currentValue?: number): TimingChoice[] {
  const attempts =
    currentValue !== undefined &&
    currentValue > 0 &&
    !LISTED_HINT_ATTEMPTS.includes(currentValue)
      ? [...LISTED_HINT_ATTEMPTS, currentValue].sort((a, b) => a - b)
      : LISTED_HINT_ATTEMPTS;

  return [HINTS_ALWAYS, ...attempts, HINTS_WITH_SOLUTIONS].map((value) => ({
    label: describeHintSetting(value),
    value,
  }));
}
